import { createContext, useContext, useState, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { getFirestore, onSnapshot, type Unsubscribe } from 'firebase/firestore'
import type { ProjectDoc, ProjectEntity } from '@common/models/project/project_model'
import { toProjectEntity } from '@common/models/project/project_model'
import { ProjectService } from '@common/services/project_service'
import { getFirebaseApp } from '@common/config/firebase.config'
import type { SchoolAccount } from '@common/models/account/school/school-account.modl'
import { fetchActiveSchoolAccountsByIds } from '@/services/school-account-lookup'
import { useAuth } from './AuthContext'
import { useAdminAccount } from './AdminAccountContext'

export type ProjectWithSchool = ProjectEntity & {
  schoolAccountData: SchoolAccount | null;
};

type ProjectContextType = {
  projects: ProjectEntity[];
  projectsWithSchool: ProjectWithSchool[];
  isLoading: boolean;
  error: Error | null;
};

const ProjectContext = createContext<ProjectContextType>({ 
  projects: [], 
  projectsWithSchool: [],
  isLoading: true,
  error: null,
});

export const ProjectProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { adminAccount, isLoading: adminLoading } = useAdminAccount();
  const [projects, setProjects] = useState<ProjectEntity[]>([]);
  const [schoolAccountMap, setSchoolAccountMap] = useState<Map<string, SchoolAccount>>(new Map())
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const indexErrorLoggedRef = useRef(false);
  const loadingSchoolIdsRef = useRef<Set<string>>(new Set())

  // collection group の read は Firestore ルールで isAdmin() のときのみ許可されているため、未ログイン・非管理者では購読しない
  const canSubscribeProjects = Boolean(user && adminAccount);

  // Projectに学校情報を付与
  const projectsWithSchool = useMemo(() => {
    return projects.map((project) => {
      const schoolAccountId = project.school_account_id ?? null;
      const schoolAccountData = schoolAccountId ? schoolAccountMap.get(schoolAccountId) || null : null;
      return {
        ...project,
        schoolAccountData,
      } as ProjectWithSchool;
    });
  }, [projects, schoolAccountMap]);

  // 全学校横断で進行中案件を collectionGroup クエリで監視（管理者ログイン時のみ購読）
  useEffect(() => {
    if (!canSubscribeProjects) {
      setProjects([]);
      setSchoolAccountMap(new Map())
      setError(null);
      setIsLoading(adminLoading);
      return;
    }

    setError(null);
    setIsLoading(true);
    indexErrorLoggedRef.current = false;

    const db = getFirestore(getFirebaseApp());
    const q = ProjectService.getActiveProjectsQuery(db);

    const unsubscribe: Unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const list: ProjectEntity[] = [];
        snapshot.docs.forEach((docSnap) => {
          try {
            const entity = toProjectEntity(docSnap.id, docSnap.data() as ProjectDoc, docSnap.ref.path);
            const pathParts = docSnap.ref.path.split('/');
            if (pathParts[0] === 'school_accounts' && pathParts.length >= 2) {
              entity.school_account_id = pathParts[1];
            }
            list.push(entity);
          } catch (err) {
            console.error('Project parse error:', err);
          }
        });
        setProjects(list);
        setIsLoading(false);
      },
      (err) => {
        const isIndexError = String(err).includes('index') || (err as { code?: string })?.code === 'failed-precondition';
        if (!indexErrorLoggedRef.current && isIndexError) {
          indexErrorLoggedRef.current = true;
          console.error('Failed to subscribe active projects (index required). Create the index from the URL in the error below:', err);
        } else if (!isIndexError) {
          console.error('Failed to subscribe active projects:', err);
        }
        setError(err instanceof Error ? err : new Error('案件の取得に失敗しました'));
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [canSubscribeProjects, adminLoading]);

  useEffect(() => {
    const schoolIds = Array.from(
      new Set(
        projects
          .map((project) => project.school_account_id)
          .filter((id): id is string => Boolean(id)),
      ),
    )

    if (schoolIds.length === 0) {
      loadingSchoolIdsRef.current.clear()
      setSchoolAccountMap((prev) => (prev.size === 0 ? prev : new Map()))
      return
    }

    // 不要な学校情報を間引く
    setSchoolAccountMap((prev) => {
      let changed = false
      const next = new Map<string, SchoolAccount>()
      schoolIds.forEach((id) => {
        const account = prev.get(id)
        if (account) next.set(id, account)
      })
      if (next.size !== prev.size) {
        changed = true
      } else {
        for (const id of prev.keys()) {
          if (!next.has(id)) {
            changed = true
            break
          }
        }
      }
      if (!changed) return prev
      return next
    })

    const missingIds = schoolIds.filter(
      (id) => !schoolAccountMap.has(id) && !loadingSchoolIdsRef.current.has(id),
    )
    if (missingIds.length === 0) return

    missingIds.forEach((id) => loadingSchoolIdsRef.current.add(id))

    let cancelled = false
    const fetchSchoolAccounts = async () => {
      try {
        const fetchedMap = await fetchActiveSchoolAccountsByIds(missingIds)

        if (cancelled) return
        setSchoolAccountMap((prev) => {
          const next = new Map(prev)
          fetchedMap.forEach((value, key) => next.set(key, value))
          return next
        })
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch school accounts for projects:', err)
          setError(err instanceof Error ? err : new Error('学校情報の取得に失敗しました'))
        }
      } finally {
        missingIds.forEach((id) => loadingSchoolIdsRef.current.delete(id))
      }
    }

    fetchSchoolAccounts()

    return () => {
      cancelled = true
    }
  }, [projects, schoolAccountMap]);

  return (
    <ProjectContext.Provider value={{ projects, projectsWithSchool, isLoading, error }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
